import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Lock, Eye, Users, Database } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
              Privacy
              <br />
              <span className="font-bold">Policy</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
              How we collect, use, and protect your information
            </p>
            <div className="flex items-center justify-center gap-4 text-gray-400">
              <Shield className="w-5 h-5" />
              <span>Last updated: August 15, 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                At StartupStreak ("we," "our," or "us"), we are committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our platform and services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our services, you agree to the collection and use of information in accordance 
                with this policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Scope:</strong> This policy applies to our website, web application, communications, and any
                related APIs or integrations we operate. Additional terms may apply as described in our
                <Link to="/terms" className="text-blue-600 underline ml-1">Terms of Service</Link>.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-black mb-4">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect personal information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Name and contact information (email address, phone number)</li>
                <li>Account credentials and profile information</li>
                <li>Business idea details and validation responses</li>
                <li>Communication preferences and feedback</li>
                <li>Payment and billing information</li>
              </ul>

              <h3 className="text-xl font-medium text-black mb-4">Usage Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We automatically collect certain information about your use of our services, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Log data and device information</li>
                <li>Usage patterns and feature interactions</li>
                <li>Performance data and error reports</li>
                <li>IP address and location data</li>
              </ul>

              <h3 className="text-xl font-medium text-black mb-4">AI and Machine Learning</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our platform uses artificial intelligence and machine learning to provide personalized 
                business validation and recommendations. This includes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Analysis of your business ideas and market research</li>
                <li>Personalized task generation and accountability tracking</li>
                <li>Content recommendations and playbook suggestions</li>
                <li>Performance analytics and improvement insights</li>
              </ul>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and manage your account</li>
                <li>Send you important updates and notifications</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Analyze usage patterns and optimize user experience</li>
                <li>Develop new features and services</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">AI-Powered Features</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our AI features process your information to provide personalized business insights:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li><strong>Idea Validation:</strong> Your business ideas are analyzed using AI to provide 
                market feedback, competitive analysis, and profitability assessments.</li>
                <li><strong>Task Generation:</strong> AI creates personalized daily tasks based on your 
                sprint progress and business goals.</li>
                <li><strong>Content Recommendations:</strong> AI suggests relevant playbooks, templates, 
                and resources based on your business type and stage.</li>
                <li><strong>Performance Analytics:</strong> AI analyzes your progress and provides 
                insights to improve your sprint outcomes.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                All AI processing is done securely and in accordance with industry best practices. 
                Your data is used solely to improve your experience and provide relevant business insights.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Information Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties, 
                except in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li><strong>Service Providers:</strong> We may share information with trusted third-party 
                service providers who assist us in operating our platform.</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law 
                or to protect our rights and safety.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger or acquisition, 
                your information may be transferred as part of the business assets.</li>
                <li><strong>Consent:</strong> We may share information with your explicit consent.</li>
              </ul>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Secure data centers and infrastructure</li>
                <li>Employee training on data protection</li>
              </ul>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Your Rights and Choices</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Processing:</strong> Control how your data is processed by AI systems</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                To exercise these rights, please contact our Data Protection Officer at
                <a href="mailto:dpo@startupstreak.com" className="text-blue-600 underline ml-1">dpo@startupstreak.com</a>.
                EU/EEA users can learn more about their rights on our
                <Link to="/gdpr" className="text-blue-600 underline ml-1">GDPR page</Link>.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to provide our services 
                and fulfill the purposes outlined in this policy. We may retain certain information 
                for longer periods to comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for basic platform functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use our services</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Marketing Cookies:</strong> Deliver relevant content and advertisements</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You can control cookie preferences through your browser settings, though disabling 
                certain cookies may affect platform functionality.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                For full details on the types of cookies we use and how to manage your preferences, see our
                <Link to="/cookie-policy" className="text-blue-600 underline ml-1">Cookie Policy</Link>.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not intended for children under 13 years of age. We do not knowingly 
                collect personal information from children under 13. If you become aware that a child 
                has provided us with personal information, please contact us immediately.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. 
                We ensure that such transfers comply with applicable data protection laws and implement 
                appropriate safeguards to protect your information.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new policy on this page and updating the "Last updated" date. 
                Your continued use of our services after any changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-medium text-black mb-6">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> privacy@startupstreak.com
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Address:</strong> StartupStreak, Privacy Team
                </p>
                <p className="text-gray-700">
                  <strong>Response Time:</strong> We aim to respond to all privacy-related inquiries within 48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              Questions About Privacy?
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              We're committed to transparency and protecting your data. 
              Reach out if you need clarification on any privacy matters.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-startup-teal text-black hover:bg-startup-gold hover:text-startup-navy text-lg px-12 py-4 font-medium transition-all hover:ring-2 hover:ring-startup-gold/60">
                  Contact Us
                </Button>
              </Link>
              <Link to="/signup">
                <Button size="lg" variant="outline" className="border-startup-teal text-black hover:bg-white hover:text-startup-navy hover:border-white text-lg px-12 py-4 font-medium transition-all">
                  Start Your Streak
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
